import React from 'react';
import { ServiceSlug } from '../../data/services';

interface ServiceMockScreenshotProps {
  slug: ServiceSlug;
  className?: string;
}

const accent = '#ff8c00';
const bg = '#121212';
const panel = '#1a1a1a';
const border = '#2e2e2e';
const text = '#f2f2f2';
const muted = '#8a8a8a';

const MockShell: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
    <rect width="800" height="500" fill={bg} />
    <rect x="0" y="0" width="800" height="36" fill="#0d0d0d" />
    <circle cx="18" cy="18" r="5" fill="#ff5f57" />
    <circle cx="36" cy="18" r="5" fill="#febc2e" />
    <circle cx="54" cy="18" r="5" fill="#28c840" />
    <rect x="90" y="12" width="220" height="12" rx="6" fill="#242424" />
    <text x="24" y="58" fill={text} fontSize="14" fontWeight="700" fontFamily="system-ui, sans-serif">
      {title}
    </text>
    {children}
  </svg>
);

const mocks: Record<ServiceSlug, React.ReactNode> = {
  'internal-software': (
    <MockShell title="Operations CRM">
      <rect x="24" y="72" width="150" height="404" rx="10" fill={panel} stroke={border} />
      {['Dashboard', 'Clients', 'Workflows', 'Reports'].map((item, i) => (
        <text key={item} x="44" y={110 + i * 34} fill={i === 1 ? accent : muted} fontSize="12" fontFamily="system-ui, sans-serif">
          {item}
        </text>
      ))}
      <rect x="194" y="72" width="582" height="404" rx="10" fill={panel} stroke={border} />
      <rect x="214" y="92" width="120" height="56" rx="8" fill="#1f1f1f" stroke={border} />
      <rect x="350" y="92" width="120" height="56" rx="8" fill="#1f1f1f" stroke={border} />
      <rect x="486" y="92" width="120" height="56" rx="8" fill="#241a0d" stroke={accent} />
      <text x="228" y="116" fill={muted} fontSize="10" fontFamily="system-ui, sans-serif">Active users</text>
      <text x="228" y="136" fill={text} fontSize="18" fontWeight="700" fontFamily="system-ui, sans-serif">1,284</text>
      <text x="364" y="116" fill={muted} fontSize="10" fontFamily="system-ui, sans-serif">Open tasks</text>
      <text x="364" y="136" fill={text} fontSize="18" fontWeight="700" fontFamily="system-ui, sans-serif">47</text>
      <text x="500" y="116" fill={muted} fontSize="10" fontFamily="system-ui, sans-serif">Uptime</text>
      <text x="500" y="136" fill={accent} fontSize="18" fontWeight="700" fontFamily="system-ui, sans-serif">99.9%</text>
      {[0, 1, 2, 3, 4].map((row) => (
        <g key={row}>
          <rect x="214" y={170 + row * 52} width="542" height="40" rx="6" fill={row % 2 ? '#171717' : '#151515'} />
          <rect x="228" y={182 + row * 52} width="80" height="16" rx="4" fill="#2a2a2a" />
          <rect x="360" y={182 + row * 52} width="120" height="16" rx="4" fill="#2a2a2a" />
          <rect x="520" y={182 + row * 52} width="60" height="16" rx="8" fill={row === 0 ? '#1f3d1f' : '#2a2a2a'} />
        </g>
      ))}
    </MockShell>
  ),
  'process-automation': (
    <MockShell title="Workflow builder">
      <rect x="60" y="110" width="130" height="64" rx="12" fill={panel} stroke={accent} />
      <text x="82" y="148" fill={text} fontSize="12" fontFamily="system-ui, sans-serif">Web form</text>
      <path d="M190 142 H250" stroke={accent} strokeWidth="2" />
      <rect x="250" y="110" width="130" height="64" rx="12" fill={panel} stroke={border} />
      <text x="268" y="148" fill={text} fontSize="12" fontFamily="system-ui, sans-serif">CRM sync</text>
      <path d="M380 142 H440" stroke={accent} strokeWidth="2" />
      <rect x="440" y="110" width="130" height="64" rx="12" fill={panel} stroke={border} />
      <text x="458" y="148" fill={text} fontSize="12" fontFamily="system-ui, sans-serif">Slack alert</text>
      <path d="M570 142 H630" stroke={accent} strokeWidth="2" />
      <rect x="630" y="110" width="110" height="64" rx="12" fill="#241a0d" stroke={accent} />
      <text x="648" y="148" fill={accent} fontSize="12" fontFamily="system-ui, sans-serif">Email</text>
      <rect x="120" y="240" width="560" height="200" rx="12" fill={panel} stroke={border} />
      <text x="140" y="270" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">Last 24h runs</text>
      {[40, 70, 55, 90, 65, 110, 85].map((h, i) => (
        <rect key={i} x={160 + i * 60} y={400 - h} width="28" height={h} rx="4" fill={i === 5 ? accent : '#3a3a3a'} />
      ))}
    </MockShell>
  ),
  'chatbots-virtual-agents': (
    <MockShell title="Support assistant">
      <rect x="40" y="80" width="300" height="380" rx="12" fill={panel} stroke={border} />
      <rect x="60" y="110" width="180" height="36" rx="10" fill="#242424" />
      <text x="72" y="133" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">How do I reset access?</text>
      <rect x="120" y="170" width="200" height="80" rx="10" fill="#241a0d" stroke={accent} />
      <text x="136" y="198" fill={text} fontSize="11" fontFamily="system-ui, sans-serif">I can reset permissions</text>
      <text x="136" y="218" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">and send a secure link.</text>
      <rect x="360" y="80" width="400" height="380" rx="12" fill={panel} stroke={border} />
      <text x="380" y="110" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">Live conversations</text>
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <rect x="380" y={130 + i * 72} width="360" height="56" rx="8" fill="#171717" />
          <circle cx="404" cy={158 + i * 72} r="14" fill="#2a2a2a" />
          <rect x="430" y={144 + i * 72} width="140" height="12" rx="4" fill="#2f2f2f" />
          <rect x="430" y={162 + i * 72} width="90" height="10" rx="4" fill="#242424" />
          <rect x="680" y={150 + i * 72} width="44" height="18" rx="9" fill={i === 0 ? '#1f3d1f' : '#2a2a2a'} />
        </g>
      ))}
    </MockShell>
  ),
  'rags-knowledge-systems': (
    <MockShell title="Company knowledge">
      <rect x="40" y="80" width="220" height="380" rx="12" fill={panel} stroke={border} />
      {['Handbook.pdf', 'SOP wiki', 'Pricing.docx', 'API docs'].map((doc, i) => (
        <g key={doc}>
          <rect x="56" y={110 + i * 64} width="188" height="48" rx="8" fill={i === 0 ? '#241a0d' : '#171717'} stroke={i === 0 ? accent : border} />
          <text x="72" y={140 + i * 64} fill={text} fontSize="11" fontFamily="system-ui, sans-serif">{doc}</text>
        </g>
      ))}
      <rect x="280" y="80" width="480" height="380" rx="12" fill={panel} stroke={border} />
      <text x="300" y="110" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">Ask your knowledge base</text>
      <rect x="300" y="130" width="440" height="48" rx="10" fill="#171717" stroke={border} />
      <text x="316" y="160" fill={text} fontSize="12" fontFamily="system-ui, sans-serif">What is our refund policy for enterprise?</text>
      <rect x="300" y="200" width="440" height="220" rx="10" fill="#241a0d" stroke={accent} />
      <text x="316" y="230" fill={accent} fontSize="11" fontWeight="700" fontFamily="system-ui, sans-serif">Answer sourced from Handbook.pdf</text>
      <text x="316" y="258" fill={text} fontSize="11" fontFamily="system-ui, sans-serif">Enterprise clients receive a 30-day review window</text>
      <text x="316" y="278" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">with prorated credit after onboarding completion.</text>
    </MockShell>
  ),
  'custom-dashboards': (
    <MockShell title="Executive dashboard">
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x={24 + i * 200} y="80" width="180" height="72" rx="10" fill={panel} stroke={i === 2 ? accent : border} />
          <text x={40 + i * 200} y="106" fill={muted} fontSize="10" fontFamily="system-ui, sans-serif">
            {['Revenue', 'Expenses', 'Margin'][i]}
          </text>
          <text x={40 + i * 200} y="132" fill={text} fontSize="20" fontWeight="700" fontFamily="system-ui, sans-serif">
            {['$284k', '$96k', '66%'][i]}
          </text>
        </g>
      ))}
      <rect x="24" y="170" width="500" height="290" rx="12" fill={panel} stroke={border} />
      <polyline points="60,400 120,360 180,370 240,300 300,310 360,240 420,250 480,200" fill="none" stroke={accent} strokeWidth="3" />
      <rect x="540" y="170" width="236" height="140" rx="12" fill={panel} stroke={border} />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x="560" y={230 + i * 22} width={160 - i * 20} height="12" rx="4" fill={i === 0 ? accent : '#3a3a3a'} />
      ))}
      <rect x="540" y="330" width="236" height="130" rx="12" fill={panel} stroke={border} />
      <circle cx="658" cy="395" r="48" fill="none" stroke="#3a3a3a" strokeWidth="16" />
      <circle cx="658" cy="395" r="48" fill="none" stroke={accent} strokeWidth="16" strokeDasharray="120 180" transform="rotate(-90 658 395)" />
    </MockShell>
  ),
  'forecasting-classification': (
    <MockShell title="Demand forecast">
      <rect x="24" y="80" width="752" height="220" rx="12" fill={panel} stroke={border} />
      <polyline points="50,250 130,230 210,210 290,200 370,170 450,160 530,140 610,120 690,100" fill="none" stroke="#555" strokeWidth="2" strokeDasharray="6 4" />
      <polyline points="50,250 130,240 210,220 290,205 370,190 450,175 530,155 610,130 690,110" fill="none" stroke={accent} strokeWidth="3" />
      <text x="50" y="110" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">Actual</text>
      <text x="120" y="110" fill={accent} fontSize="11" fontFamily="system-ui, sans-serif">Forecast</text>
      <rect x="24" y="320" width="360" height="164" rx="12" fill={panel} stroke={border} />
      {['High intent', 'At risk', 'Churn', 'Upsell'].map((label, i) => (
        <g key={label}>
          <text x="44" y={360 + i * 32} fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">{label}</text>
          <rect x="160" y={346 + i * 32} width={80 + i * 30} height="14" rx="4" fill={i === 3 ? accent : '#3a3a3a'} />
        </g>
      ))}
      <rect x="404" y="320" width="372" height="164" rx="12" fill="#241a0d" stroke={accent} />
      <text x="424" y="360" fill={accent} fontSize="12" fontWeight="700" fontFamily="system-ui, sans-serif">Next quarter +18%</text>
      <text x="424" y="384" fill={text} fontSize="11" fontFamily="system-ui, sans-serif">Model confidence 92%</text>
    </MockShell>
  ),
  'websites-virtual-businesses': (
    <MockShell title="Brand landing page">
      <rect x="0" y="36" width="800" height="464" fill="#0a0a0a" />
      <text x="80" y="140" fill={text} fontSize="28" fontWeight="700" fontFamily="system-ui, sans-serif">Launch faster.</text>
      <text x="80" y="175" fill={accent} fontSize="28" fontWeight="700" fontFamily="system-ui, sans-serif">Convert better.</text>
      <rect x="80" y="210" width="280" height="12" rx="4" fill="#2a2a2a" />
      <rect x="80" y="232" width="220" height="12" rx="4" fill="#242424" />
      <rect x="80" y="280" width="120" height="36" rx="18" fill={accent} />
      <text x="104" y="303" fill="#fff" fontSize="12" fontWeight="600" fontFamily="system-ui, sans-serif">Get started</text>
      <rect x="420" y="100" width="300" height="300" rx="16" fill={panel} stroke={border} />
      <rect x="440" y="130" width="260" height="24" rx="6" fill="#242424" />
      <rect x="440" y="170" width="200" height="140" rx="8" fill="#1f1f1f" />
      <rect x="440" y="330" width="120" height="40" rx="8" fill="#241a0d" stroke={accent} />
    </MockShell>
  ),
  'technical-seo-audit': (
    <MockShell title="SEO technical report">
      <circle cx="140" cy="200" r="70" fill="none" stroke="#2e2e2e" strokeWidth="14" />
      <circle cx="140" cy="200" r="70" fill="none" stroke={accent} strokeWidth="14" strokeDasharray="320 120" transform="rotate(-90 140 200)" />
      <text x="108" y="208" fill={text} fontSize="28" fontWeight="700" fontFamily="system-ui, sans-serif">84</text>
      <text x="250" y="120" fill={text} fontSize="16" fontWeight="700" fontFamily="system-ui, sans-serif">Priority fixes</text>
      {['Meta titles missing', 'Slow LCP on mobile', 'Broken internal links', 'Schema incomplete', 'Images without alt'].map((fix, i) => (
        <g key={fix}>
          <rect x="250" y={140 + i * 44} width="500" height="34" rx="8" fill={i < 2 ? '#241a0d' : '#171717'} stroke={i < 2 ? accent : border} />
          <text x="266" y={162 + i * 44} fill={text} fontSize="11" fontFamily="system-ui, sans-serif">{fix}</text>
          <text x="700" y={162 + i * 44} fill={i < 2 ? accent : muted} fontSize="11" fontFamily="system-ui, sans-serif">P{i + 1}</text>
        </g>
      ))}
    </MockShell>
  ),
  'meta-ads-audit': (
    <MockShell title="Meta ads review">
      <rect x="40" y="90" width="280" height="340" rx="12" fill={panel} stroke={border} />
      <rect x="60" y="110" width="240" height="140" rx="8" fill="#1f1f1f" />
      <text x="72" y="280" fill={text} fontSize="12" fontWeight="700" fontFamily="system-ui, sans-serif">Active creative A</text>
      <text x="72" y="302" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">CTR 0.8% · CVR 1.2%</text>
      <rect x="340" y="90" width="420" height="340" rx="12" fill={panel} stroke={border} />
      <text x="360" y="120" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">Message match score</text>
      <text x="360" y="155" fill={accent} fontSize="32" fontWeight="700" fontFamily="system-ui, sans-serif">62/100</text>
      {['Headline mismatch', 'CTA too generic', 'Landing slow'].map((issue, i) => (
        <g key={issue}>
          <rect x="360" y={190 + i * 52} width="380" height="40" rx="8" fill="#171717" stroke={border} />
          <text x="376" y={216 + i * 52} fill={text} fontSize="11" fontFamily="system-ui, sans-serif">{issue}</text>
        </g>
      ))}
    </MockShell>
  ),
  'global-digital-audit': (
    <MockShell title="360° digital score">
      {['Web', 'Social', 'Offer', 'Funnel', 'Brand'].map((domain, i) => (
        <g key={domain}>
          <text x={60 + i * 140} y="110" fill={muted} fontSize="10" fontFamily="system-ui, sans-serif">{domain}</text>
          <rect x={60 + i * 140} y="120" width="110" height="80" rx="10" fill={panel} stroke={border} />
          <text x={78 + i * 140} y="170" fill={i === 2 ? accent : text} fontSize="22" fontWeight="700" fontFamily="system-ui, sans-serif">
            {[78, 71, 54, 82, 69][i]}
          </text>
        </g>
      ))}
      <rect x="40" y="230" width="720" height="220" rx="12" fill={panel} stroke={border} />
      <polygon points="400,260 520,300 480,380 320,380 280,300" fill="rgba(255,140,0,0.15)" stroke={accent} strokeWidth="2" />
      <text x="60" y="430" fill={accent} fontSize="12" fontWeight="700" fontFamily="system-ui, sans-serif">Top action: align offer messaging with landing page</text>
    </MockShell>
  ),
  'automated-prospecting': (
    <MockShell title="B2B lead pipeline">
      <rect x="24" y="80" width="752" height="48" rx="8" fill="#171717" />
      {['Company', 'City', 'Score', 'Status'].map((col, i) => (
        <text key={col} x={40 + i * 180} y="110" fill={muted} fontSize="11" fontWeight="700" fontFamily="system-ui, sans-serif">{col}</text>
      ))}
      {[0, 1, 2, 3, 4].map((row) => (
        <g key={row}>
          <rect x="24" y={140 + row * 56} width="752" height="44" rx="6" fill={row % 2 ? '#151515' : '#171717'} />
          <rect x="40" y={154 + row * 56} width="120" height="14" rx="4" fill="#2f2f2f" />
          <rect x="220" y={154 + row * 56} width="80" height="14" rx="4" fill="#2a2a2a" />
          <rect x="400" y={154 + row * 56} width="36" height="14" rx="4" fill={row < 2 ? accent : '#3a3a3a'} />
          <rect x="580" y={152 + row * 56} width="70" height="18" rx="9" fill={row === 0 ? '#241a0d' : '#242424'} stroke={row === 0 ? accent : border} />
        </g>
      ))}
    </MockShell>
  ),
  'custom-ai-tools': (
    <MockShell title="Custom AI workspace">
      <rect x="40" y="80" width="200" height="380" rx="12" fill={panel} stroke={border} />
      {['Quote builder', 'CSV importer', 'LinkedIn scan', 'Doc parser'].map((tool, i) => (
        <g key={tool}>
          <rect x="56" y={110 + i * 72} width="168" height="52" rx="8" fill={i === 0 ? '#241a0d' : '#171717'} stroke={i === 0 ? accent : border} />
          <text x="72" y={142 + i * 72} fill={text} fontSize="11" fontFamily="system-ui, sans-serif">{tool}</text>
        </g>
      ))}
      <rect x="260" y="80" width="500" height="380" rx="12" fill={panel} stroke={accent} />
      <text x="280" y="115" fill={accent} fontSize="12" fontWeight="700" fontFamily="system-ui, sans-serif">Quote builder · running</text>
      <rect x="280" y="140" width="460" height="280" rx="10" fill="#111" />
      <text x="300" y="180" fill={muted} fontSize="11" fontFamily="system-ui, sans-serif">Input: client brief + pricing sheet</text>
      <text x="300" y="220" fill={text} fontSize="11" fontFamily="system-ui, sans-serif">Output: branded PDF quote ready to send</text>
      <rect x="300" y="250" width="200" height="120" rx="8" fill="#1c1c1c" stroke={border} />
    </MockShell>
  ),
};

const ServiceMockScreenshot: React.FC<ServiceMockScreenshotProps> = ({ slug, className }) => (
  <div className={className}>{mocks[slug]}</div>
);

export default ServiceMockScreenshot;
